import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiRequestService, apiEndpoints } from './api-request.service';
import { LocalStorageService, AUTH_TOKEN, CURRENT_USER } from './local-storage.service';
import { AccountModel } from 'src/app/modules/accounts/models/account.model';
import { BaseService } from './base.service';
import { FormErrorService } from './form-error.service';
import { AppSettingsService } from './app-settings.service';
import { of, catchError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService extends BaseService {
    public currentUser$ = new BehaviorSubject<AccountModel | null>(null);
    updatedUser$: BehaviorSubject<AccountModel | null> = new BehaviorSubject<AccountModel | null>(null);
    onSaveSuccess$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


    constructor(
        protected _apiRequestService: ApiRequestService,
        protected _formErrorService: FormErrorService,
        protected _appSettingsService: AppSettingsService,
    private _localStorageService: LocalStorageService
    ) {
        super(_formErrorService, _appSettingsService, _apiRequestService);

    const userData = localStorage.getItem(CURRENT_USER);
    if (userData) {
      this.currentUser$.next(JSON.parse(userData));
    }
}

    public loadPermissions(): Observable<boolean> {
        // Solicitare profil user cu permisiuni rol
        return this._apiRequestService.submitGetRequest(apiEndpoints.userProfile).pipe(
            map((res: any) => {
                const userData = res.data;
                if (userData) {
                    // Date existente + permisiuni din profil
                    const currentUser = this.currentUser$.getValue();
                    const updatedUser = { 
                        ...currentUser, 
                        ...userData,
                        permissions: userData.permissions || userData.permissions_names || []
                    };
                    this.currentUser$.next(updatedUser);
                    this._localStorageService.setValue(CURRENT_USER, JSON.stringify(updatedUser));
                }
                return true;
            }),
            catchError(err => {
                console.error('Failed to load user profile with permissions:', err);
                // Fallback la permissions-list
                return this._apiRequestService.submitGetRequest(apiEndpoints.permissions).pipe(
                    map((res: any) => {
                        const currentUser = this.currentUser$.getValue();
                        if (currentUser) {
                            const updatedUser = { ...currentUser, permissions: res.data || [] };
                            this.currentUser$.next(updatedUser);
                        }
                        return true;
                    }),
                    catchError(() => of(false))
                );
            })
        );
    }

    // Refresh token JWT
    refreshToken() {
        this._apiRequestService.submitGetRequest(apiEndpoints.refreshToken).subscribe({
                next: apiResponse => {
            this._localStorageService.setValue(AUTH_TOKEN, apiResponse.data.token);
        },
        error: error => this.reportError('Error on refresh token', error)
        });
    }

    // Verificare permisiune user
    checkForPermission(permission: string): Observable<boolean> {
        return this.currentUser$.pipe(
        map(user => !!user?.permissions?.includes(permission))
        );
    }

    // Userul curent din API
    getUser() {
        this._apiRequestService.submitGetRequest(apiEndpoints.userProfile).subscribe({
            next: apiResponse => {
                this.updatedUser$.next(apiResponse.data);
                this.currentUser$.next(apiResponse.data);
            },
            error: error => this.reportError('Error on get current user', error)
        });
    }

    // Actualizare user curent prin API
    updateUser(user: AccountModel) {
        this._apiRequestService.submitPutRequest(apiEndpoints.userProfile, user).subscribe({
            next: apiResponse => {
                this.updatedUser$.next(apiResponse.data);
                this.currentUser$.next(apiResponse.data);
                this.onSaveSuccess$.next(true);
            },
            error: error => this.reportError('Error on update current user', error)
        });
    }

    protected reportError(message: string, error: any) {
        console.error(message, error);
        this._appSettingsService.isLoading.set(false);
    }

}